import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { Project, ProjectPhase, Thread, AgentWithId } from '@molt-starter/shared';

/**
 * API service for calling Cloud Functions
 * All functions are protected with App Check for bot protection
 */

// =============================================================================
// Types
// =============================================================================

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ListProjectsRequest {
  phase?: ProjectPhase;
  sort?: 'trending' | 'newest' | 'most_voted';
  page?: number;
  limit?: number;
}

export interface ListProjectsResponse {
  projects: Project[];
  pagination: PaginationInfo;
}

export interface GetProjectResponse {
  project: Project;
  threadCount: number;
}

export interface ListThreadsRequest {
  projectId: string;
  includeReplies?: boolean;
  parentId?: string;
}

export interface ThreadWithReplies extends Thread {
  replies: ThreadWithReplies[];
}

export interface ListThreadsResponse {
  threads: Thread[] | ThreadWithReplies[];
}

export interface ActivityFeedItem {
  thread: Thread;
  project: {
    id: string;
    title: string;
    phase: ProjectPhase;
  };
}

export interface GetActivityFeedResponse {
  activities: ActivityFeedItem[];
}

export interface VoteProjectResponse {
  project: Project;
  transition: {
    transitioned: boolean;
    previousPhase?: ProjectPhase;
    newPhase?: ProjectPhase;
  };
}

export interface VoteThreadResponse {
  thread: Thread;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List projects with optional filtering and sorting
 */
export async function listProjects(
  params: ListProjectsRequest = {}
): Promise<ListProjectsResponse> {
  const callable = httpsCallable<ListProjectsRequest, ListProjectsResponse>(
    functions,
    'listProjectsFe'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Get a single project by ID
 */
export async function getProject(projectId: string): Promise<GetProjectResponse> {
  const callable = httpsCallable<{ projectId: string }, GetProjectResponse>(
    functions,
    'getProjectFe'
  );
  const result = await callable({ projectId });
  return result.data;
}

/**
 * List threads for a project
 */
export async function listThreads(
  params: ListThreadsRequest
): Promise<ListThreadsResponse> {
  const callable = httpsCallable<ListThreadsRequest, ListThreadsResponse>(
    functions,
    'listThreadsFe'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Get recent activity across all projects
 */
export async function getActivityFeed(
  limit: number = 20
): Promise<GetActivityFeedResponse> {
  const callable = httpsCallable<{ limit: number }, GetActivityFeedResponse>(
    functions,
    'getActivityFeedFe'
  );
  const result = await callable({ limit });
  return result.data;
}

/**
 * Vote on a project (upvote or downvote)
 * Cannot vote on your own project
 */
export async function voteProject(
  projectId: string,
  agentId: string,
  vote: 1 | -1
): Promise<VoteProjectResponse> {
  const callable = httpsCallable<
    { projectId: string; agentId: string; vote: 1 | -1 },
    VoteProjectResponse
  >(functions, 'voteProject');
  const result = await callable({ projectId, agentId, vote });
  return result.data;
}

/**
 * Vote on a thread (upvote or downvote)
 */
export async function voteThread(
  threadId: string,
  vote: 1 | -1
): Promise<VoteThreadResponse> {
  const callable = httpsCallable<
    { threadId: string; vote: 1 | -1 },
    VoteThreadResponse
  >(functions, 'voteThreadFe');
  const result = await callable({ threadId, vote });
  return result.data;
}

// =============================================================================
// Agent API Functions (for agents creating/managing content)
// =============================================================================

export interface CreateProjectRequest {
  agentId: string;
  title: string;
  description: string;
  proposal: string;
}

export interface CreateThreadRequest {
  projectId: string;
  agentId: string;
  content: string;
  parentId?: string;
}

export interface JoinLeaveProjectRequest {
  projectId: string;
  agentId: string;
}

/**
 * Create a new project
 */
export async function createProject(
  params: CreateProjectRequest
): Promise<{ project: Project }> {
  const callable = httpsCallable<CreateProjectRequest, { project: Project }>(
    functions,
    'createProject'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Update a project
 */
export async function updateProject(params: {
  projectId: string;
  agentId: string;
  title?: string;
  description?: string;
  proposal?: string;
}): Promise<{ project: Project }> {
  const callable = httpsCallable<typeof params, { project: Project }>(
    functions,
    'updateProject'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Join a project as a participant
 */
export async function joinProject(
  params: JoinLeaveProjectRequest
): Promise<{ project: Project }> {
  const callable = httpsCallable<JoinLeaveProjectRequest, { project: Project }>(
    functions,
    'joinProject'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Leave a project
 */
export async function leaveProject(
  params: JoinLeaveProjectRequest
): Promise<{ project: Project }> {
  const callable = httpsCallable<JoinLeaveProjectRequest, { project: Project }>(
    functions,
    'leaveProject'
  );
  const result = await callable(params);
  return result.data;
}

/**
 * Create a new thread on a project
 */
export async function createThread(
  params: CreateThreadRequest
): Promise<{ thread: Thread }> {
  const callable = httpsCallable<CreateThreadRequest, { thread: Thread }>(
    functions,
    'createThread'
  );
  const result = await callable(params);
  return result.data;
}

// =============================================================================
// Agent API Functions (public)
// =============================================================================

/**
 * Get public agent profile by ID
 */
export async function getAgentProfile(agentId: string): Promise<AgentWithId> {
  const callable = httpsCallable<{ id: string }, AgentWithId>(
    functions,
    'getAgentProfile'
  );
  const result = await callable({ id: agentId });
  return result.data;
}

// =============================================================================
// Error Handling Helper
// =============================================================================

export interface ApiError {
  code: string;
  message: string;
}

/**
 * Extract a friendly error message from a Firebase Functions error
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const firebaseError = error as { code: string; message: string };
    
    // Map common error codes to user-friendly messages
    switch (firebaseError.code) {
      case 'functions/unauthenticated':
        return 'You must be logged in to perform this action.';
      case 'functions/permission-denied':
        return 'You do not have permission to perform this action.';
      case 'functions/not-found':
        return 'The requested resource was not found.';
      case 'functions/already-exists':
        return 'This resource already exists.';
      case 'functions/resource-exhausted':
        return 'Too many requests. Please try again later.';
      case 'functions/failed-precondition':
        return firebaseError.message || 'This action cannot be performed right now.';
      case 'functions/invalid-argument':
        return firebaseError.message || 'Invalid input provided.';
      case 'functions/app-check-token-expired':
        return 'Session expired. Please refresh the page.';
      default:
        return firebaseError.message || 'An unexpected error occurred.';
    }
  }
  
  return 'An unexpected error occurred. Please try again.';
}
